import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, getDocs, query, orderBy, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import PacoteModal from './PacoteModal';

// Imports dos componentes MUI
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Componente de confirmação
const ConfirmacaoToast = ({ closeToast, onConfirm, message }) => (
    <div>
      <p>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
        <Button size="small" variant="contained" color="error" onClick={() => { onConfirm(); closeToast(); }}>Sim, Excluir</Button>
        <Button size="small" variant="outlined" color="inherit" onClick={closeToast}>Não</Button>
      </div>
    </div>
);

const Pacotes = () => {
  const [pacotes, setPacotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pacoteParaEditar, setPacoteParaEditar] = useState(null);
  
  const fetchPacotes = async () => {
    setLoading(true);
    try {
      const pacotesCollectionRef = collection(db, 'pacotes');
      const q = query(pacotesCollectionRef, orderBy('dataInicial', 'desc'));
      const querySnapshot = await getDocs(q);
      const pacotesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPacotes(pacotesData);
    } catch (error) {
      console.error("Erro ao buscar pacotes: ", error);
      toast.error("Erro ao carregar os pacotes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPacotes();
  }, []);

  const handleSavePacote = async (dadosDoPacote) => {
    try {
      if (pacoteParaEditar) {
        const pacoteRef = doc(db, 'pacotes', pacoteParaEditar.id);
        await updateDoc(pacoteRef, dadosDoPacote);
        toast.success('Pacote atualizado com sucesso!');
      } else {
        await addDoc(collection(db, 'pacotes'), dadosDoPacote);
        toast.success('Pacote criado com sucesso!');
      }
      setIsModalOpen(false);
      setPacoteParaEditar(null);
      fetchPacotes();
    } catch (error) {
      console.error("Erro ao salvar pacote: ", error);
      toast.error("Erro ao salvar pacote.");
    }
  };

  const handleExcluirPacote = (pacoteId, pacoteNome) => {
    const deletar = async () => {
        try {
            await deleteDoc(doc(db, 'pacotes', pacoteId));
            toast.success('Pacote excluído com sucesso!');
            fetchPacotes();
        } catch (error) {
            console.error("Erro ao excluir pacote: ", error);
            toast.error("Erro ao excluir pacote.");
        }
    };

    toast.warn(
      <ConfirmacaoToast 
        message={`Tem certeza que deseja excluir o pacote "${pacoteNome}"?`}
        onConfirm={deletar}
      />, {
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    });
  };

  const abrirModal = (pacote = null) => {
    setPacoteParaEditar(pacote);
    setIsModalOpen(true);
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    const [ano, mes, dia] = dataString.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <Box sx={{ padding: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Paper sx={{ padding: 2, marginBottom: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h1" fontWeight="600">
            Gestão de Pacotes
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => abrirModal()}
          >
            Novo Pacote
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabela de pacotes">
          <TableHead>
            <TableRow>
              <TableCell sx={{fontWeight: 'bold'}}>Nome do Pacote</TableCell>
              <TableCell sx={{fontWeight: 'bold'}}>Período</TableCell>
              <TableCell sx={{fontWeight: 'bold'}}>Valor Total</TableCell>
              <TableCell sx={{fontWeight: 'bold'}}>Status</TableCell>
              <TableCell align="center" sx={{fontWeight: 'bold'}}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow><TableCell colSpan={5} align="center">Carregando...</TableCell></TableRow>
            ) : pacotes.map(pacote => (
              <TableRow key={pacote.id} hover>
                <TableCell>
                  <Typography variant="body1" fontWeight="500">{pacote.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">{pacote.descricao}</Typography>
                </TableCell>
                <TableCell>{formatarData(pacote.dataInicial)} a {formatarData(pacote.dataFinal)}</TableCell>
                <TableCell>{(pacote.valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                <TableCell>
                  <Chip 
                    label={pacote.ativo ? 'Ativo' : 'Inativo'}
                    color={pacote.ativo ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton color="primary" onClick={() => abrirModal(pacote)} title="Editar Pacote">
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleExcluirPacote(pacote.id, pacote.nome)} title="Excluir Pacote">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <PacoteModal 
        open={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setPacoteParaEditar(null); }}
        onSave={handleSavePacote}
        pacoteParaEditar={pacoteParaEditar}
      />
    </Box>
  );
};

export default Pacotes;

